// Notice.jsx — THE one way the app tells you something went wrong (ERR-1, Alex
// 2026-07-26: "I was speaking in general").
//
// Before this there were four presentations for one job: 41 alert() popups, 3
// window.confirm, 43 console-only errors, and 23 inline warnings. The popup is the
// worst of them — it is browser chrome, not app chrome; it blocks the page; it is
// DETACHED from the row that failed; and dismissing it destroys the message, so
// there is nothing to re-read while you fix the problem.
//
// Alex's rule: the message belongs BESIDE THE ACTION — next to Save/Cancel, or in
// the row it concerns — never in a floating box.
//
// The words are already handled server-side (src/db-errors.js turns a constraint
// failure into a sentence). This is the PLACE half.
//
// Deliberately NOT a toast: a toast disappears on a timer, and an error you are
// still working on must stay until dismissed or superseded.
import { WARN, OK, DELETE } from '../icons.js';
import { BTN_GLYPH } from '../theme-tokens.js';

const KIND = {
  error:   { icon: WARN, color: 'var(--th-status-deprecated)' },
  warning: { icon: WARN, color: 'var(--th-status-draft)' },
  success: { icon: OK,   color: 'var(--th-status-synced)' },
};

// kind: 'error' | 'warning' | 'success'   (default 'error')
// onDismiss: () => void — omit for a message that should not be dismissable
// inline: true renders bare (for sitting next to a button); false adds a framed
//         block for view-level placement above a grid.
export default function Notice({ kind = 'error', children, onDismiss, inline = false, title }) {
  if (!children) return null;
  const k = KIND[kind] || KIND.error;
  const body = (
    <span style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 6, minWidth: 0 }}>
      <span aria-hidden="true" style={{ color: k.color, flexShrink: 0 }}>{k.icon}</span>
      <span style={{ color: 'var(--th-text-body)', wordBreak: 'break-word' }}>{children}</span>
    </span>
  );
  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      title={title}
      style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 8, fontSize: 'var(--fs-small)', minWidth: 0,
        ...(inline ? { padding: 0 } : {
          padding: '6px 10px', marginBottom: 6, borderRadius: 3,
          border: `1px solid ${k.color}`, background: 'var(--th-highlight-bg)',
        }),
      }}
    >
      {body}
      {onDismiss && (
        <button
          onClick={onDismiss}
          aria-label="Dismiss message"
          title="Dismiss"
          style={{ ...BTN_GLYPH, flexShrink: 0, color: 'var(--th-text-secondary)', lineHeight: 1 }}
        >{DELETE}</button>
      )}
    </div>
  );
}
